/**
 * components/client/SaveAndQuitScreen.tsx
 *
 * Confirmation screen displayed when a client clicks "Sauvegarder et quitter".
 * Reassures the client their progress is saved and explains how to resume.
 *
 * Same visual language as ThankYouScreen (centered card, animated entry)
 * but with a calm, informational tone rather than celebratory.
 *
 * Created June 2026: mobile-first form UX improvements.
 */

import { BookOpen } from 'lucide-react';
import ClientBrandingHeader from '../branding/ClientBrandingHeader';

interface SaveAndQuitScreenProps {
  logoUrl: string | null;
  senderDisplayName: string | null;
}

export default function SaveAndQuitScreen({ logoUrl, senderDisplayName }: SaveAndQuitScreenProps) {
  return (
    <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md text-center animate-welcome-enter">
        {logoUrl && (
          <div className="flex justify-center mb-8">
            <ClientBrandingHeader
              logoUrl={logoUrl}
              displayName={null}
              size="lg"
            />
          </div>
        )}

        <div className="relative inline-flex items-center justify-center w-24 h-24 bg-primary-100 rounded-full mb-8 animate-check-pop">
          <BookOpen className="w-12 h-12 text-primary-600" />
        </div>

        <h2 className="text-3xl font-bold text-neutral-900 mb-4 animate-thank-you-text-1">
          Formulaire sauvegardé
        </h2>

        <p className="text-lg text-neutral-600 leading-relaxed mb-6 animate-thank-you-text-2">
          Vos réponses ont bien été enregistrées.
          {senderDisplayName && (
            <>{' '}Vous pouvez reprendre le formulaire de{' '}
            <span className="font-semibold text-neutral-700">{senderDisplayName}</span> à tout moment.
            </>
          )}
        </p>

        <div className="bg-neutral-50 border border-neutral-200 rounded-2xl p-5 text-sm text-neutral-500 animate-thank-you-text-2">
          <p className="font-medium text-neutral-700 mb-1">Comment reprendre ?</p>
          <p>Ouvrez à nouveau le lien que vous avez reçu pour continuer là où vous vous êtes arrêté.</p>
        </div>
      </div>
    </div>
  );
}
